/**
 * Accordion component for expandable/collapsible content sections
 * Provides interactive accordion functionality with smooth animations
 *
 * @class Accordion
 */
class Accordion {
    /**
     * Creates an instance of Accordion
     *
     * @param {HTMLElement} elem - The accordion container element
     */
    constructor(elem) {
        this.accordion = elem;
        this.accordionItems = elem.querySelectorAll('.accordion__item');
    }

    /**
     * Initializes the accordion component
     * Sets up event listeners and initial state for all accordion items
     */
    init = () => {
        // Initialize each accordion item
        this.accordionItems.forEach((item) => {
            const summary = item.querySelector('.accordion__summary');
            const panel = item.querySelector('.accordion__panel');

            // Add click event listener to summary
            summary.addEventListener('click', (event) => {
                event.preventDefault();
                this.toggleAccordion(item, summary, panel);
            });

            // Set initial max height based on open state
            this.setMaxHeight(panel, item.open ? panel.scrollHeight : 0);
        });
    };

    /**
     * Sets the max height of an accordion panel using CSS custom properties
     *
     * @param {HTMLElement} panel - The accordion panel element
     * @param {number} heightInPx - The height to set in pixels
     */
    setMaxHeight = (panel, heightInPx) => {
        // Convert pixel height to rem and set CSS variable
        const heightInRem = heightInPx / 10;
        panel.style.setProperty('--content-max-height', `${heightInRem}rem`);
    };

    /**
     * Opens an accordion item
     *
     * @param {HTMLDetailsElement} item - The accordion item element
     * @param {HTMLElement} summary - The accordion summary element
     * @param {HTMLElement} panel - The accordion panel element
     */
    openAccordion = (item, summary, panel) => {
        // Open the accordion item
        item.classList.add('accordion__item--open');
        item.setAttribute('open', '');
        this.setMaxHeight(panel, panel.scrollHeight);
    };

    /**
     * Closes an accordion item
     *
     * @param {HTMLDetailsElement} item - The accordion item element
     * @param {HTMLElement} summary - The accordion summary element
     * @param {HTMLElement} panel - The accordion panel element
     */
    closeAccordion = (item, summary, panel) => {
        // Close the accordion item
        item.classList.remove('accordion__item--open');
        this.setMaxHeight(panel, 0);
        // Remove 'open' attribute after transition ends
        panel.addEventListener(
            'transitionend',
            () => {
                item.removeAttribute('open');
            },
            { once: true },
        );
    };

    /**
     * Toggles the open/closed state of an accordion item
     *
     * @param {HTMLDetailsElement} item - The accordion item element
     * @param {HTMLElement} summary - The accordion summary element
     * @param {HTMLElement} panel - The accordion panel element
     */
    toggleAccordion = (item, summary, panel) => {
        // Toggle accordion state based on current open status
        item.open
            ? this.closeAccordion(item, summary, panel)
            : this.openAccordion(item, summary, panel);
    };
}

export default Accordion;
